import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Github, Calendar, Briefcase, User, Share2, Check, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Navbar from "@/components/public/Navbar";
import Footer from "@/components/public/Footer";

export default function ProjectDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [profile, setProfile] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let mounted = true;
        Promise.all([api.get(`/projects/${slug}`), api.get("/profile"), api.get("/settings")])
            .then(([p, pr, st]) => {
                if (!mounted) return;
                setProject(p.data);
                setProfile(pr.data);
                setSettings(st.data);
            })
            .catch(() => setNotFound(true))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink-900">
                <div className="mono-label">Loading project…</div>
            </div>
        );
    }
    if (notFound || !project) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink-900 text-white flex-col gap-4">
                <div className="font-display text-3xl">Project not found</div>
                <Link to="/" className="text-brand underline">Back home</Link>
            </div>
        );
    }

    return (
        <div data-testid="project-detail-page" className="min-h-screen bg-ink-900 text-white">
            <Navbar profile={profile} settings={settings} />
            <main className="pt-32 pb-24">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-mono text-ink-300 mb-8">
                        <Link to="/" className="hover:text-white transition-colors">Home</Link>
                        <ChevronRight size={12} />
                        <Link to="/work" className="hover:text-white transition-colors">Work</Link>
                        <ChevronRight size={12} />
                        <span className="text-brand truncate max-w-[200px] sm:max-w-xs">{project.title}</span>
                    </nav>

                    <Link
                        to="/work"
                        data-testid="project-back"
                        className="inline-flex items-center gap-2 text-ink-200 hover:text-brand transition-colors mb-8"
                    >
                        <ArrowLeft size={16} /> Back to all projects
                    </Link>

                    <div className="flex flex-wrap items-center gap-2 mb-6">
                        <span className="font-mono text-xs px-2.5 py-1 rounded-full border border-white/10 text-ink-100">
                            {project.category}
                        </span>
                        {project.featured && (
                            <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-brand text-white">Featured</span>
                        )}
                        {project.client?.includes("Kodekami") && (
                            <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-brand/20 border border-brand/50 text-brand-light font-medium flex items-center gap-1 shadow-[0_0_12px_rgba(255,69,0,0.3)]">
                                <Sparkles size={12} className="text-brand" /> Kodekami Ecosystem
                            </span>
                        )}
                        {project.status && (
                            <span
                                className={`font-mono text-xs px-2.5 py-1 rounded-full border ${
                                    project.status === "Active"
                                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                                        : project.status === "Maintenance"
                                        ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                                        : project.status === "Archived"
                                        ? "bg-zinc-500/15 border-zinc-500/40 text-zinc-300"
                                        : "bg-white/5 border-white/15 text-ink-100"
                                }`}
                            >
                                {project.status}
                            </span>
                        )}
                        <span className="mono-label">· {project.year}</span>
                    </div>

                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tighter leading-[0.92]">
                        {project.title}
                    </h1>
                    <p className="mt-6 text-lg text-ink-200 max-w-3xl">{project.summary}</p>

                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        {project.demo_url && (
                            <a
                                href={project.demo_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="project-demo-link"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white font-medium hover:bg-brand-hover transition-colors"
                            >
                                <ExternalLink size={16} /> View live
                            </a>
                        )}
                        {project.repo_url && (
                            <a
                                href={project.repo_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="project-repo-link"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors"
                            >
                                <Github size={16} /> Source code
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                                setCopied(true);
                                toast.success("Project link copied to clipboard!");
                                setTimeout(() => setCopied(false), 2000);
                            }}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors text-sm font-medium"
                            title="Copy project link"
                        >
                            {copied ? (
                                <>
                                    <Check size={16} className="text-emerald-400" />
                                    <span className="text-emerald-400">Link copied!</span>
                                </>
                            ) : (
                                <>
                                    <Share2 size={16} /> Share project
                                </>
                            )}
                        </button>
                    </div>

                    {project.cover_image && (
                        <div className="mt-12 rounded-2xl overflow-hidden border border-white/5 bg-ink-800 aspect-[16/9]">
                            <img src={project.cover_image} alt={project.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </div>
                    )}

                    <div className="mt-16 grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-8">
                            <div className="mono-label mb-4">About the project</div>
                            <div className="prose prose-invert max-w-none">
                                <p className="text-ink-100 leading-relaxed whitespace-pre-wrap text-lg">{project.description}</p>
                            </div>
                        </div>
                        <aside className="lg:col-span-4 space-y-6">
                            <DetailRow icon={Calendar} label="Year" value={project.year} />
                            <DetailRow
                                icon={User}
                                label="Client"
                                value={
                                    project.client?.includes("Kodekami") ? (
                                        <span className="text-brand font-medium">{project.client}</span>
                                    ) : (
                                        project.client || "—"
                                    )
                                }
                            />
                            <DetailRow icon={Briefcase} label="Role" value={project.role || "—"} />
                            <div className="p-5 rounded-lg border border-white/5 bg-ink-800/40">
                                <div className="mono-label mb-3">Tech stack</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {project.tech_stack?.map((t) => (
                                        <span
                                            key={t}
                                            className="font-mono text-xs px-2.5 py-1 rounded bg-ink-700/60 border border-white/5 text-ink-100"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    </div>

                    {project.gallery?.length > 0 && (
                        <div className="mt-20">
                            <div className="mono-label mb-6">Gallery</div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                {project.gallery.map((g, i) => (
                                    <div key={i} className="rounded-xl overflow-hidden border border-white/5 bg-ink-800">
                                        <img src={g} alt={`${project.title} ${i + 1}`} className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <Footer profile={profile} />
        </div>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-4 p-4 rounded-lg border border-white/5 bg-ink-800/40">
            <div className="w-9 h-9 rounded-md bg-ink-700/60 border border-white/5 flex items-center justify-center text-brand">
                <Icon size={14} />
            </div>
            <div className="min-w-0">
                <div className="mono-label">{label}</div>
                <div className="text-white font-medium truncate">{value}</div>
            </div>
        </div>
    );
}
